// Appels HTTP typés vers /api/billing/* — souscription Hsasoft du tenant.
//
// Trois routes côté backend (cf. app/routes/billing.py, Lot 8.5.3) :
// - GET  /api/billing/me     → état courant (sub + BI pending + plan + grâce)
// - POST /api/billing/start  → souscrit à un plan + génère un lien Ziina
// - POST /api/billing/cancel → annule à terme (par défaut) ou immédiatement
//
// Les types ci-dessous sont alignés sur app/schemas/billing.py. À régénérer
// manuellement si les schémas backend changent.

import { api } from "./client";
import type { PlanPublic, UUID } from "./types";

// ---- Subscription --------------------------------------------------------

export type SubscriptionStatus =
  | "incomplete"
  | "active"
  | "past_due"
  | "canceled";

export type SubscriptionPublic = {
  id: UUID;
  status: SubscriptionStatus;
  plan_id: UUID;
  started_at: string; // ISO datetime UTC
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
};

// ---- BillingInvoice ------------------------------------------------------

export type BillingInvoiceStatus = "pending" | "paid" | "void";

export type BillingInvoicePublic = {
  id: UUID;
  reference: string;
  status: BillingInvoiceStatus;
  period_start: string; // YYYY-MM-DD
  period_end: string;
  issued_at: string; // ISO datetime
  due_date: string; // YYYY-MM-DD
  // Decimals sérialisés en string (même convention que InvoicePublic) pour
  // éviter les pertes de précision JS sur les centimes.
  amount_ht: string;
  vat_amount: string;
  amount_ttc: string;
  currency: string;
  ziina_external_ref: string | null;
  ziina_checkout_url: string | null;
  ziina_link_expires_at: string | null;
  paid_at: string | null;
};

// ---- /api/billing/start --------------------------------------------------

export type BillingStartPayload = {
  /** Slug du plan choisi (ex. "pro", "business"). */
  plan_slug: string;
};

export type BillingStartResponse = {
  subscription: SubscriptionPublic;
  billing_invoice: BillingInvoicePublic;
  /** Raccourci : `billing_invoice.ziina_checkout_url` dupliqué pour la redirection. */
  checkout_url: string;
};

// ---- /api/billing/me -----------------------------------------------------

export type BillingMeResponse = {
  /** `null` ⇒ tenant n'a jamais souscrit (en trial ou free). */
  subscription: SubscriptionPublic | null;
  plan: PlanPublic | null;
  /** Dernière BI PENDING — sert à afficher "Payer maintenant" au retour sur la page. */
  pending_billing_invoice: BillingInvoicePublic | null;
  /** True si on est entre `current_period_end` et `+ GRACE_PERIOD_DAYS`. */
  in_grace_period: boolean;
};

// ---- /api/billing/cancel -------------------------------------------------

export type BillingCancelPayload = {
  /** False (défaut) = annulation à l'échéance. True = downgrade immédiat (admin). */
  immediately?: boolean;
};

export type BillingCancelResponse = {
  subscription: SubscriptionPublic;
};

// ---- API -----------------------------------------------------------------

export const billingApi = {
  me: () => api.get<BillingMeResponse>("/api/billing/me"),

  start: (payload: BillingStartPayload) =>
    api.post<BillingStartResponse>("/api/billing/start", payload),

  cancel: (payload: BillingCancelPayload = {}) =>
    api.post<BillingCancelResponse>("/api/billing/cancel", payload),
};


// ---- /api/billing/invoices (ajouté Lot 8.6) ------------------------------

export const billingInvoicesApi = {
  /** Historique complet des BillingInvoices du tenant (toutes, PAID + PENDING). */
  list: () => api.get<BillingInvoicePublic[]>('/api/billing/invoices'),

  /** URL de téléchargement PDF pour une BillingInvoice (redirect 302 → S3, ou bytes directs). */
  pdfUrl: (id: UUID) => `/api/billing/invoices/${id}/pdf`,
};
